import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import syncService from '../services/syncService';
import { useAuth } from '../contexts/AuthContext';
import Spinner from './shared/Spinner';
import ReportActions from './shared/ReportActions';
import ReportAnnotation from './shared/ReportAnnotation';
import SyncConnectStatusReport from './SyncConnectStatusReport';
import './OnsiteInstructionsPage.css';

// PROJECT-LEVEL on-site instructions — every extraction task that has records, in one report.
//
// WHY IT IS SEPARATE FROM THE TASK REPORT. A property does not think in tasks. Working through a
// migration they want one list of what to fix, in the order the migration runs; opening eleven tasks to
// find the three with anything in them is the job this removes. A task with no records contributes
// nothing and is not mentioned, which is why the report reads as short as the work actually is.
//
// THE REPORT IS BUILT ON THE SERVER (/opt/core/onsite-instructions) and this page renders it. That is
// not an implementation detail: the same report is emailed on a schedule, a scheduled send has no
// browser, and two copies of the classifiers would eventually tell the property two different things.
// So this file deliberately contains NO classification logic — if an instruction reads wrongly, it is
// wrong in the util layer, for the screen and the email alike.

export default function ProjectOnsiteReportPage() {
    const params = useParams();
    const syncId = params.syncId || params.syncId;
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [report, setReport] = useState(null);
    const [errorsOnly, setErrorsOnly] = useState(false);
    // Any daily schedule running for this project (V446). Shown on the page, not just inside the email
    // dialog, so "this property is getting a mail every morning" is visible without opening anything.
    const [schedules, setSchedules] = useState([]);
    // OPERATOR NOTES (V368), keyed by TASK and group — the project report spans several tasks, so the
    // task uuid has to be part of the key or a note written on one section would appear on another
    // section that happens to share a group key. Seeded from the report (the server already resolves
    // them) and updated in place as they are edited, so a save does not need the report rebuilt.
    const [annotations, setAnnotations] = useState({});
    const { isSuperAdmin } = useAuth();

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true); setError(null);
            try {
                const data = await syncService.getProjectOnsiteReport(syncId, errorsOnly);
                if (!cancelled) {
                    setReport(data || null);
                    const seeded = {};
                    for (const sc of (data?.scopes || [])) {
                        for (const g of (sc.groups || [])) {
                            if (g.annotation) seeded[`${sc.taskId}::onsite:${sc.scope}:${g.meta.key}`] = g.annotation;
                        }
                    }
                    setAnnotations(seeded);
                }
            } catch (err) {
                if (!cancelled) setError(err.response?.data?.message || err.message || 'The report could not be built');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [syncId, errorsOnly]);

    const loadSchedules = useCallback(async () => {
        if (!isSuperAdmin) return;
        try {
            const r = await syncService.getReportRecipients(syncId, report?.tasks?.[0]?.taskId || '');
            setSchedules(Array.isArray(r?.schedules) ? r.schedules : []);
        } catch (_) { /* the schedule strip is informational; never block the report on it */ }
    }, [syncId, isSuperAdmin, report]);

    useEffect(() => { loadSchedules(); }, [loadSchedules]);

    const stopSchedule = async (id) => {
        try {
            await syncService.stopReportSchedule(syncId, id);
            setSchedules(prev => prev.filter(s => s.id !== id));
        } catch (err) {
            setError(err.response?.data?.message || 'Could not stop the schedule');
        }
    };

    const scopes = report?.scopes || [];
    const projectName = report?.project || 'Sync project';
    const safeName = String(projectName).replace(/[^\w.-]+/g, '-').toLowerCase();

    // The report as flat rows — one per record, carrying the instruction it belongs to. The CSV is what
    // a property works from on a clipboard, so it repeats the instruction on every line rather than
    // making someone match a record back to a heading three screens up.
    const buildCsv = useMemo(() => () => {
        const esc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
        const head = ['Task', 'Scope', 'Item', 'Value', 'Fix in', 'Instruction', 'Source record', 'Target record', 'Status'];
        const rows = [head.join(',')];
        for (const s of scopes) {
            for (const g of s.groups) {
                for (const r of g.records) {
                    rows.push([
                        s.taskName || '', s.scope, g.meta.label, r.detail || '',
                        g.meta.system === 'source' ? report.sourceSystemName : g.meta.system === 'target' ? report.targetSystemName : '',
                        g.meta.instruction, r.sourceRecordId || '', r.targetRecordId || '', r.recordStatus || '',
                    ].map(esc).join(','));
                }
            }
        }
        return rows.join('\n');
    }, [scopes, report]);

    return (
        <div className="onsite-page">
            <div className="onsite-toolbar">
                <button className="onsite-back" onClick={() => navigate(`/sync/${syncId}`)}>
                    <ArrowLeft size={15} /> Project
                </button>

                <label className="onsite-toggle">
                    <input type="checkbox" checked={errorsOnly} onChange={e => setErrorsOnly(e.target.checked)} />
                    <span>Errors only (hide warnings)</span>
                </label>

                <ReportActions
                    syncId={syncId}
                    /* The email and its saved recipients belong to the PROJECT, not to a task — this
                       report spans all of them. The task uuid is the first task with records, purely
                       because the existing email route is addressed per task; the recipients it saves
                       are keyed on the project either way (V445). */
                    taskId={report?.tasks?.[0]?.taskId || ''}
                    /* Enabled on an EMPTY report too. "Nothing needs correcting at the property" is a
                       result worth sending — it is how a hotel learns it is done — and a disabled
                       button with no explanation reads as the screen being broken. */
                    disabled={loading || !report}
                    noun="on-site instructions"
                    defaultSubject={`[${projectName}] On-site fixes required${errorsOnly ? ' (errors)' : ''}`}
                    defaultIntro="Please action the on-site fixes listed below, then let us know so we can reprocess the affected records."
                    csvFilename={`${safeName}-onsite-instructions${errorsOnly ? '-errors' : ''}.csv`}
                    buildCsv={buildCsv}
                    /* THE EMAIL IS BUILT ON THE SERVER, not from this page.
                       The body carries a heading and a count per extraction task; every record id goes
                       into an attached PDF — a migration produces thousands of them and inlining them
                       made a message clients truncate and nobody could read. The same report is emailed
                       on a schedule, where there is no browser to build anything, so the two would have
                       been separate implementations of the same document. See /opt/core/onsite-render. */
                    /* allowEmpty says "I am looking at an all-clear". The server refuses an empty
                       build otherwise, because an empty build can equally mean it disagrees with this
                       screen — and mailing a property "all clear" on the back of a disagreement is
                       worse than failing. */
                    serverReport={{ report: 'project-onsite', errorsOnly, allowEmpty: scopes.length === 0 }}
                    /* A send may have just created a schedule; the strip above is the only place it
                       shows once the dialog has closed. */
                    onSent={() => loadSchedules()}
                />
            </div>

            {/* A daily schedule is a thing that keeps happening to somebody else's inbox. It belongs on
                the page, not only inside the dialog that created it — otherwise the only way to find out
                a property is being mailed every morning is to open the email dialog and look. */}
            {schedules.length > 0 && (
                <div className="onsite-schedule sa-only-panel">
                    {schedules.map(s => (
                        <div key={s.id} className="onsite-schedule-row">
                            <span className="onsite-schedule-text">
                                Emailed daily to {(s.recipients || []).length} recipient{(s.recipients || []).length === 1 ? '' : 's'}
                                {' '}until <strong>{s.endsOn ? String(s.endsOn).slice(0, 10) : '—'}</strong>
                                {s.lastSentOn && <> · last sent {String(s.lastSentOn).slice(0, 10)}</>}
                                {s.lastError && <span className="onsite-schedule-err"> · last run failed: {s.lastError}</span>}
                            </span>
                            <button className="onsite-schedule-stop" onClick={() => stopSchedule(s.id)}>Stop</button>
                        </div>
                    ))}
                </div>
            )}

            {loading ? (
                <div className="onsite-loading"><Spinner /> Building on-site instructions…</div>
            ) : error ? (
                <div className="onsite-denied"><AlertCircle size={20} /><span>{error}</span></div>
            ) : (
                <div className="onsite-sheet">
                    <header className="onsite-header">
                        <h1 className="onsite-title">{projectName} — On-site Instructions</h1>
                        <p className="onsite-sub">
                            Every extraction task on this project that has records{errorsOnly ? ', errors only' : ''}.
                            Each item names the system to log into and the records it applies to.
                        </p>
                        {report?.generatedAt && (
                            <div className="onsite-generated">
                                Generated {new Date(report.generatedAt).toLocaleString()}
                                {report.total ? ` · ${report.total} record${report.total === 1 ? '' : 's'}` : ''}
                            </div>
                        )}
                    </header>

                    {/* THE INSTALLATION FIRST — is the property's SyncConnect up, and is OXI talking to it — as
                        its own health page reports it. Renders nothing for a project without SyncConnect. */}
                    <SyncConnectStatusReport syncId={syncId} />

                    {/* WHAT WENT THROUGH, before what did not.
                        Without it the page reads as a list of problems with no denominator — 3 records
                        to fix could be 3 of 12 or 3 of 12,000 — and an all-clear says nothing at all.
                        Same counts as the project screen and the emailed copy. */}
                    {(report?.summary || []).length > 0 && (
                        <section className="onsite-summary">
                            <h2 className="onsite-scope-title">Records synced</h2>
                            <table className="onsite-summary-table">
                                <thead>
                                    <tr><th>Task</th><th>Processed</th><th>Warnings</th><th>Errors</th><th>Skipped</th><th>In flight</th><th>Total</th><th>Complete</th></tr>
                                </thead>
                                <tbody>
                                    {report.summary.map(r => (
                                        <tr key={r.taskId || r.scope}>
                                            <td className="onsite-summary-task">{r.taskName || r.scope}</td>
                                            <td>{r.processed.toLocaleString()}</td>
                                            <td className={r.warnings ? 'onsite-summary-warn' : ''}>{r.warnings.toLocaleString()}</td>
                                            <td className={r.errors ? 'onsite-summary-err' : ''}>{r.errors.toLocaleString()}</td>
                                            <td>{r.skipped.toLocaleString()}</td>
                                            <td>{r.inFlight.toLocaleString()}</td>
                                            <td><strong>{r.total.toLocaleString()}</strong></td>
                                            <td>{r.completePercent}%</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td>Total</td>
                                        <td>{report.totals.processed.toLocaleString()}</td>
                                        <td className={report.totals.warnings ? 'onsite-summary-warn' : ''}>{report.totals.warnings.toLocaleString()}</td>
                                        <td className={report.totals.errors ? 'onsite-summary-err' : ''}>{report.totals.errors.toLocaleString()}</td>
                                        <td>{report.totals.skipped.toLocaleString()}</td>
                                        <td>{report.totals.inFlight.toLocaleString()}</td>
                                        <td><strong>{report.totals.total.toLocaleString()}</strong></td>
                                        <td />
                                    </tr>
                                </tfoot>
                            </table>
                        </section>
                    )}

                    {scopes.length === 0 ? (
                        <p className="onsite-empty">
                            {errorsOnly
                                ? 'No errors — nothing failed that needs correcting at the property.'
                                : 'No on-site items — nothing needs correcting at the property.'}
                        </p>
                    ) : scopes.map(s => (
                        <section key={s.scope} className="onsite-scope">
                            <h2 className="onsite-scope-title">
                                {s.taskName || s.scope} <span className="onsite-scope-count">{s.count}</span>
                            </h2>
                            {s.groups.map(g => (
                                <div key={g.meta.key} className="onsite-group">
                                    <div className="onsite-group-head">
                                        <span className="onsite-group-label">{g.meta.label}</span>
                                        {g.meta.code ? <span className="onsite-code">{g.meta.code}</span> : null}
                                        <span className="onsite-group-count">{g.records.length}</span>
                                        {/* The values behind the group, from the server's own summary —
                                            the same list the email and the PDF carry (HIL-90). */}
                                        {g.meta.varies && (g.meta.values || []).length > 1 ? (
                                            <span className="onsite-values" title="Each value, and how many records name it">
                                                {g.meta.values.map(v => (
                                                    <span key={v.value} className="onsite-value">
                                                        {v.value}{v.count > 1 ? <b> ×{v.count}</b> : null}
                                                    </span>
                                                ))}
                                            </span>
                                        ) : null}
                                    </div>
                                    <p className="onsite-instruction">{g.meta.instruction}</p>
                                    {/* The same note store and the same key as the task-level report
                                        (V368), so a note written on either is the one note. */}
                                    <ReportAnnotation
                                        syncId={syncId}
                                        taskId={s.taskId}
                                        groupKey={`onsite:${s.scope}:${g.meta.key}`}
                                        value={annotations[`${s.taskId}::onsite:${s.scope}:${g.meta.key}`] || ''}
                                        onChange={(key, text) => setAnnotations(prev => {
                                            const next = { ...prev };
                                            const full = `${s.taskId}::${key}`;
                                            if (text) next[full] = text; else delete next[full];
                                            return next;
                                        })}
                                        onError={setError}
                                    />
                                    <p className="onsite-ids">
                                        {g.records.map(r => r.sourceRecordId).filter(Boolean).join(', ')}
                                    </p>
                                </div>
                            ))}
                        </section>
                    ))}
                </div>
            )}
        </div>
    );
}
